import {
  Action,
  createSelector,
  Selector,
  State,
  StateContext
} from '@ngxs/store';
import { Injectable } from '@angular/core';
import { Customer } from '@module-customer/models/model-customer';
import * as actions from '@module-claims/store/claims.actions';
import * as pharmacyActions from '@module-claims/store/pharmacy.actions';
import * as prescriberActions from '@module-claims/store/prescriber.actions';
import * as claimCEActions from '@module-claims/store/claim-ce.actions';
import * as mfpClaimsActions from '@module-claims/store/mfp-claims.actions';
import * as auditClaimSearchActions from '@module-claims/store/audit-claim-search.actions';
import { ClaimsHttpService } from '../services/claims-http.service';
import { catchError, map, tap } from 'rxjs/operators';
import { IClaimUpload } from '@module-claims/models/claim-upload.interface';
import { ModelPagination } from '@models-global/model-pagination';
import produce from 'immer';
import { ENUM_CLAIM_UPLOAD_STATUS } from '@module-claims/enums/enum-claim-upload-status';
import {
  IClaimFile340BAudit,
  IClaimsGroupClaimV2,
  IClaimsGroupSummaryItemV2,
  IClaimTotals340BAudit
} from '@module-claims/models/claims-group.interface';
import { IClaimDetail } from '@module-claims/models/claim.interface';
import { IClaimListElement } from '@module-claims/models/claim-list-element.interface';
import { throwError } from 'rxjs';
import { GRID_PAGE_SIZE } from '@models-global/constants';
import {
  IClaimsSummary,
  IClaimsSummaryBatchStatistics
} from '@module-claims/models/claims-summary.interface';
import { CompositeFilterDescriptor } from '@progress/kendo-data-query';
import { IClaimFileStatus } from '@module-claims/models/claim-file-status.interface';
import { BatchStatisticsLevel } from '@module-claims/enums/batch-statistics-level.enum';
import {
  AuditClaimSearchResult,
  AuditReportingResolutionTrend,
  AuditReportingStatusTotals,
  AuditReportingTop340BMetrics,
  AuditReportingTopDisputedMetrics,
  AuditReportingTopDisputeResolutionMetrics,
  AuditReportingTopNot340BMetrics,
  ClaimsGroupListParamsV2
} from '@module-claims/models/claims-http.interface';
import { PharmacyBenefitManager } from '@module-claims/models/pharmacy-benefit-manager.interface';
import { CustomerServiceType } from '@module-customer/enums/customer-service-type.enum';
import { IClaimFileLOB } from '@module-claims/models/claim-file-lob.interface';
import {
  PharmacyDetails,
  ContractPharmacyDetails,
  InhousePharmacyDetails
} from '@module-claims/models/pharmacy.interface';
import { PrescriberDetailsSimple } from '@module-claims/models/prescriber.interface';
import { ClaimsCEsGetResponse } from '@module-claims/models/claim-ce.interface';
import { MFPClaim } from '@module-claims/models/mfp-claim.interface';

export interface IData<T = any> extends ModelPagination<T> {
  pending: boolean;
  data: T;
  count?: number;
}

export interface IClaimsData<T = any> extends IData<T> {
  summary: any[];
}

export interface IDataNonPaginated<T = any> {
  pending: boolean;
  data: T;
  total: number;
}

export interface IPendingState {
  pending: boolean;
}

export interface IAsyncData<T = any> {
  pending: boolean;
  data?: T;
}

export type IClaimsBatchStatisticsState = IData & IClaimsSummaryBatchStatistics;

export interface IClaimsState {
  customersData: IData<Customer[]>;
  pbmData: IDataNonPaginated<PharmacyBenefitManager[]>;
  lobData: IDataNonPaginated<IClaimFileLOB[]>;
  pharmacyDetails: IAsyncData<PharmacyDetails>;
  contractPharmacyDetails: IAsyncData<ContractPharmacyDetails>;
  inhousePharmacyDetails: IAsyncData<InhousePharmacyDetails>;
  prescriberDetails: IAsyncData<PrescriberDetailsSimple>;
  auditClaimSearch: IAsyncData<AuditClaimSearchResult>;
  claimsUploadData: IData<IClaimUpload[]>;
  claimsUpload: IData<IClaimUpload[]>;
  claimsData: IData<IClaimListElement[]>;
  claimsCustomersData: IData<Customer[]>;
  claimsGroupsDataV2: IData<IClaimsGroupSummaryItemV2[]>;
  claimsGroupClaimsDataV2: IData<IClaimsGroupClaimV2[]>;
  claimDetailsData: IDataNonPaginated<IClaimDetail | undefined>;
  claimUpdateData: IDataNonPaginated<any>;
  auditClaimUpdate: IPendingState;
  auditClaimManualReviewComplete: IPendingState;
  claimsSummaryData: IData<IClaimsSummary[]>;
  claimsSummaryBatchStatisticsData: Map<
    BatchStatisticsLevel,
    IClaimsBatchStatisticsState
  >;
  claimFileDownload: IPendingState;
  claimFileDelete: IPendingState;
  claimFileStatus: IDataNonPaginated<IClaimFileStatus | undefined>;
  auditClaimFileData: IDataNonPaginated<IClaimFile340BAudit[]>;
  auditReviewedClaimFileData: IDataNonPaginated<IClaimFile340BAudit[]>;
  auditClaimTotalsData: IDataNonPaginated<IClaimTotals340BAudit | undefined>;
  auditReportingStatusTotals: IDataNonPaginated<
    AuditReportingStatusTotals | undefined
  >;
  auditReportingResolutionTrends: IDataNonPaginated<
    AuditReportingResolutionTrend[]
  >;
  auditReportingTop340B: IDataNonPaginated<
    AuditReportingTop340BMetrics | undefined
  >;
  auditReportingTopNot340B: IDataNonPaginated<
    AuditReportingTopNot340BMetrics | undefined
  >;
  auditReportingTopDisputed: IDataNonPaginated<
    AuditReportingTopDisputedMetrics | undefined
  >;
  auditReportingTopDisputeResolution: IDataNonPaginated<
    AuditReportingTopDisputeResolutionMetrics | undefined
  >;
  auditClaimsCEs: IAsyncData<ClaimsCEsGetResponse>;
  auditClaimsCEsContact: IPendingState;
  mfpClaims: IData<MFPClaim[]>;
  mfpClaimsUpdate: IPendingState;
}

const DataDefault = {
  skip: 0,
  take: GRID_PAGE_SIZE,
  total: 0,
  pending: false,
  data: []
};

const NonPaginatedDataDefault = {
  pending: false,
  data: [],
  total: 0
};

const NonPaginatedDataUndefinedDefault = {
  pending: false,
  data: undefined,
  total: 0
};

const PendingDefault = {
  pending: false
};

const AsyncDataDefault: IAsyncData = {
  pending: false
};

const ClaimDetailsDataDefault: IDataNonPaginated<IClaimDetail | undefined> = {
  pending: false,
  data: undefined,
  total: 0
};

const ClaimFileStatusDefault: IDataNonPaginated<IClaimFileStatus | undefined> =
  {
    pending: false,
    data: undefined,
    total: 0
  };

const ClaimCustomersDefault: IData<Customer[]> = {
  pending: false,
  skip: 0,
  total: 0,
  take: 0,
  data: [],
  count: 0
};

const ClaimTotalsDataDefault: IDataNonPaginated<
  IClaimTotals340BAudit | undefined
> = {
  pending: false,
  data: undefined,
  total: 0
};

const BatchLevelStatisticsDefault: IClaimsBatchStatisticsState = {
  pending: false,
  skip: 0,
  take: GRID_PAGE_SIZE,
  total: 0,
  totalClaims: 0,
  data: [],
  insights: {
    topRejectionReasons: [],
    topRejectionPharmacies: [],
    topRejectionPhysicians: []
  }
};

const ClaimsSummaryBatchStatisticsDataDefault = new Map<
  BatchStatisticsLevel,
  IClaimsBatchStatisticsState
>(
  Object.values(BatchStatisticsLevel).map((level) => [
    level,
    BatchLevelStatisticsDefault
  ])
);

const ClaimsStateDefault: IClaimsState = {
  customersData: DataDefault,
  pbmData: NonPaginatedDataDefault,
  lobData: NonPaginatedDataDefault,
  pharmacyDetails: AsyncDataDefault,
  contractPharmacyDetails: AsyncDataDefault,
  inhousePharmacyDetails: AsyncDataDefault,
  prescriberDetails: AsyncDataDefault,
  auditClaimSearch: AsyncDataDefault,
  claimsUploadData: DataDefault,
  claimsGroupsDataV2: DataDefault,
  claimsGroupClaimsDataV2: DataDefault,
  claimsData: DataDefault,
  claimsCustomersData: ClaimCustomersDefault,
  claimDetailsData: ClaimDetailsDataDefault,
  claimUpdateData: ClaimDetailsDataDefault,
  auditClaimUpdate: PendingDefault,
  auditClaimManualReviewComplete: PendingDefault,
  claimsUpload: DataDefault,
  claimsSummaryData: DataDefault,
  claimsSummaryBatchStatisticsData: ClaimsSummaryBatchStatisticsDataDefault,
  claimFileDownload: PendingDefault,
  claimFileDelete: PendingDefault,
  claimFileStatus: ClaimFileStatusDefault,
  auditClaimFileData: NonPaginatedDataDefault,
  auditReviewedClaimFileData: NonPaginatedDataDefault,
  auditClaimTotalsData: ClaimTotalsDataDefault,
  auditReportingStatusTotals: NonPaginatedDataUndefinedDefault,
  auditReportingResolutionTrends: NonPaginatedDataDefault,
  auditReportingTop340B: NonPaginatedDataUndefinedDefault,
  auditReportingTopNot340B: NonPaginatedDataUndefinedDefault,
  auditReportingTopDisputed: NonPaginatedDataUndefinedDefault,
  auditReportingTopDisputeResolution: NonPaginatedDataUndefinedDefault,
  auditClaimsCEs: AsyncDataDefault,
  auditClaimsCEsContact: PendingDefault,
  mfpClaims: DataDefault,
  mfpClaimsUpdate: PendingDefault
};

@State<IClaimsState>({
  name: 'Claims',
  defaults: ClaimsStateDefault
})
@Injectable()
export class ClaimsState {
  @Selector() static customersData(state: IClaimsState): IData<Customer[]> {
    return state.customersData;
  }
  @Selector() static pbmData(
    state: IClaimsState
  ): IDataNonPaginated<PharmacyBenefitManager[]> {
    return state.pbmData;
  }
  @Selector() static lobData(
    state: IClaimsState
  ): IDataNonPaginated<IClaimFileLOB[]> {
    return state.lobData;
  }
  @Selector() static pharmacyDetails(
    state: IClaimsState
  ): IAsyncData<PharmacyDetails> {
    return state.pharmacyDetails;
  }
  @Selector() static contractPharmacyDetails(
    state: IClaimsState
  ): IAsyncData<ContractPharmacyDetails> {
    return state.contractPharmacyDetails;
  }
  @Selector() static inhousePharmacyDetails(
    state: IClaimsState
  ): IAsyncData<InhousePharmacyDetails> {
    return state.inhousePharmacyDetails;
  }
  @Selector() static prescriberDetails(
    state: IClaimsState
  ): IAsyncData<PrescriberDetailsSimple> {
    return state.prescriberDetails;
  }
  @Selector() static auditClaimSearch(
    state: IClaimsState
  ): IAsyncData<AuditClaimSearchResult> {
    return state.auditClaimSearch;
  }
  @Selector() static claimsUploadData(
    state: IClaimsState
  ): IData<IClaimUpload[]> {
    return state.claimsUploadData;
  }
  @Selector() static claimFileDownload(state: IClaimsState): IPendingState {
    return state.claimFileDownload;
  }
  @Selector() static claimFileDelete(state: IClaimsState): IPendingState {
    return state.claimFileDelete;
  }
  @Selector() static claimFileStatus(
    state: IClaimsState
  ): IDataNonPaginated<IClaimFileStatus | undefined> {
    return state.claimFileStatus;
  }
  @Selector() static claimsGroupsDataV2(
    state: IClaimsState
  ): IData<IClaimsGroupSummaryItemV2[]> {
    return state.claimsGroupsDataV2;
  }
  @Selector() static claimsData(
    state: IClaimsState
  ): IData<IClaimListElement[]> {
    return state.claimsData;
  }
  @Selector() static claimsGroupClaimsDataV2(
    state: IClaimsState
  ): IData<IClaimsGroupClaimV2[]> {
    return state.claimsGroupClaimsDataV2;
  }
  @Selector() static claimsCustomersData(
    state: IClaimsState
  ): IData<Customer[]> {
    return state.claimsCustomersData;
  }
  @Selector() static claimDetailsData(
    state: IClaimsState
  ): IDataNonPaginated<IClaimDetail | undefined> {
    return state.claimDetailsData;
  }
  @Selector() static claimUpdateData(
    state: IClaimsState
  ): IDataNonPaginated<any> {
    return state.claimUpdateData;
  }
  @Selector() static auditClaimUpdate(state: IClaimsState): IPendingState {
    return state.auditClaimUpdate;
  }
  @Selector() static auditClaimManualReviewComplete(
    state: IClaimsState
  ): IPendingState {
    return state.auditClaimManualReviewComplete;
  }
  @Selector() static claimsSummaryData(
    state: IClaimsState
  ): IData<IClaimsSummary[]> {
    return state.claimsSummaryData;
  }
  @Selector() static auditClaimFileData(
    state: IClaimsState
  ): IDataNonPaginated<IClaimFile340BAudit[]> {
    return state.auditClaimFileData;
  }
  @Selector() static auditReviewedClaimFileData(
    state: IClaimsState
  ): IDataNonPaginated<IClaimFile340BAudit[]> {
    return state.auditReviewedClaimFileData;
  }
  @Selector() static auditClaimTotalsData(
    state: IClaimsState
  ): IDataNonPaginated<IClaimTotals340BAudit | undefined> {
    return state.auditClaimTotalsData;
  }
  @Selector() static auditReportingStatusTotals(
    state: IClaimsState
  ): IDataNonPaginated<AuditReportingStatusTotals | undefined> {
    return state.auditReportingStatusTotals;
  }
  @Selector() static auditReportingResolutionTrends(
    state: IClaimsState
  ): IDataNonPaginated<AuditReportingResolutionTrend[]> {
    return state.auditReportingResolutionTrends;
  }
  @Selector() static auditReportingTop340B(
    state: IClaimsState
  ): IDataNonPaginated<AuditReportingTop340BMetrics | undefined> {
    return state.auditReportingTop340B;
  }
  @Selector() static auditReportingTopNot340B(
    state: IClaimsState
  ): IDataNonPaginated<AuditReportingTopNot340BMetrics | undefined> {
    return state.auditReportingTopNot340B;
  }
  @Selector() static auditReportingTopDisputed(
    state: IClaimsState
  ): IDataNonPaginated<AuditReportingTopDisputedMetrics | undefined> {
    return state.auditReportingTopDisputed;
  }
  @Selector() static auditReportingTopDisputeResolution(
    state: IClaimsState
  ): IDataNonPaginated<AuditReportingTopDisputeResolutionMetrics | undefined> {
    return state.auditReportingTopDisputeResolution;
  }
  @Selector() static auditClaimsCEs(
    state: IClaimsState
  ): IAsyncData<ClaimsCEsGetResponse> {
    return state.auditClaimsCEs;
  }
  @Selector() static auditClaimsCEsContact(state: IClaimsState): IPendingState {
    return state.auditClaimsCEsContact;
  }
  @Selector() static mfpClaims(state: IClaimsState): IData<MFPClaim[]> {
    return state.mfpClaims;
  }
  @Selector() static mfpClaimsUpdate(state: IClaimsState): IPendingState {
    return state.mfpClaimsUpdate;
  }

  static claimsSummaryBatchStatisticsData(
    level: BatchStatisticsLevel
  ): (state: IClaimsState) => IClaimsBatchStatisticsState {
    return createSelector([ClaimsState], (state: IClaimsState) => {
      return state.claimsSummaryBatchStatisticsData.get(level)!;
    });
  }

  constructor(private _claimsHttpService: ClaimsHttpService) {}

  @Action(actions.ClearCustomersList)
  onClearCustomersList({ patchState }: StateContext<IClaimsState>) {
    return patchState({
      customersData: DataDefault
    });
  }

  @Action(actions.GetCustomersList)
  onGetCustomersList(
    { patchState, getState }: StateContext<IClaimsState>,
    filters?: CompositeFilterDescriptor
  ) {
    patchState({
      customersData: {
        ...getState().customersData,
        pending: true
      }
    });
    return this._claimsHttpService.getCustomersList(filters?.filters).pipe(
      tap((res) =>
        patchState({
          customersData: {
            ...res,
            pending: false
          }
        })
      )
    );
  }

  @Action(actions.GetPBMList)
  onGetPBMList({ patchState, getState }: StateContext<IClaimsState>) {
    patchState({
      pbmData: {
        ...getState().pbmData,
        pending: true
      }
    });
    return this._claimsHttpService.getPBMList().pipe(
      tap((res) =>
        patchState({
          pbmData: {
            pending: false,
            data: res,
            total: res.length
          }
        })
      ),
      catchError((e) => {
        patchState({
          pbmData: {
            ...getState().pbmData,
            pending: false
          }
        });
        return throwError(e);
      })
    );
  }

  @Action(actions.GetClaimFileLOBList)
  onGetLOBList({ patchState, getState }: StateContext<IClaimsState>) {
    patchState({
      lobData: {
        ...getState().lobData,
        pending: true
      }
    });
    return this._claimsHttpService.getLOBList().pipe(
      tap((res) =>
        patchState({
          lobData: {
            pending: false,
            data: res,
            total: res.length
          }
        })
      ),
      catchError((e) => {
        patchState({
          lobData: {
            ...getState().lobData,
            pending: false
          }
        });
        return throwError(e);
      })
    );
  }

  @Action(pharmacyActions.ClearPharmacyDetails)
  onClearPharmacyDetails({ patchState }: StateContext<IClaimsState>) {
    patchState({
      pharmacyDetails: AsyncDataDefault
    });
  }

  @Action(pharmacyActions.GetPharmacyDetails, { cancelUncompleted: true })
  onGetPharmacyDetails(
    { patchState, getState }: StateContext<IClaimsState>,
    { npi }: pharmacyActions.GetPharmacyDetails
  ) {
    patchState({
      pharmacyDetails: {
        ...getState().pharmacyDetails,
        pending: true
      }
    });
    return this._claimsHttpService.getPharmacyDetails(npi).pipe(
      tap((res) =>
        patchState({
          pharmacyDetails: {
            pending: false,
            data: res
          }
        })
      ),
      catchError((e) => {
        patchState({
          pharmacyDetails: {
            ...getState().pharmacyDetails,
            pending: false
          }
        });
        return throwError(e);
      })
    );
  }

  @Action(pharmacyActions.ClearContractPharmacyDetails)
  onClearContractPharmacyDetails({ patchState }: StateContext<IClaimsState>) {
    patchState({
      contractPharmacyDetails: AsyncDataDefault
    });
  }

  @Action(pharmacyActions.GetContractPharmacyDetails, {
    cancelUncompleted: true
  })
  onGetContractPharmacyDetails(
    { patchState, getState }: StateContext<IClaimsState>,
    { hrsaPharmacyId }: pharmacyActions.GetContractPharmacyDetails
  ) {
    patchState({
      contractPharmacyDetails: {
        ...getState().contractPharmacyDetails,
        pending: true
      }
    });
    return this._claimsHttpService
      .getContractPharmacyDetails(hrsaPharmacyId)
      .pipe(
        tap((res) =>
          patchState({
            contractPharmacyDetails: {
              pending: false,
              data: res
            }
          })
        ),
        catchError((e) => {
          patchState({
            contractPharmacyDetails: {
              ...getState().contractPharmacyDetails,
              pending: false
            }
          });
          return throwError(e);
        })
      );
  }

  @Action(pharmacyActions.LinkContractPharmacy)
  onLinkContractPharmacy(
    { patchState, getState }: StateContext<IClaimsState>,
    { params }: pharmacyActions.LinkContractPharmacy
  ) {
    patchState({
      contractPharmacyDetails: {
        ...getState().contractPharmacyDetails,
        pending: true
      },
      pharmacyDetails: {
        ...getState().pharmacyDetails,
        pending: true
      }
    });
    return this._claimsHttpService.linkContractPharmacy(params).pipe(
      tap(() =>
        patchState({
          contractPharmacyDetails: {
            ...getState().contractPharmacyDetails,
            pending: false
          },
          pharmacyDetails: {
            ...getState().pharmacyDetails,
            pending: false
          }
        })
      ),
      catchError((e) => {
        patchState({
          contractPharmacyDetails: {
            ...getState().contractPharmacyDetails,
            pending: false
          },
          pharmacyDetails: {
            ...getState().pharmacyDetails,
            pending: false
          }
        });
        return throwError(e);
      })
    );
  }

  @Action(pharmacyActions.UnlinkContractPharmacy)
  onUnlinkContractPharmacy(
    { patchState, getState }: StateContext<IClaimsState>,
    { npi, contractPharmacy }: pharmacyActions.UnlinkContractPharmacy
  ) {
    patchState({
      pharmacyDetails: {
        ...getState().pharmacyDetails,
        pending: true
      }
    });
    return this._claimsHttpService
      .unlinkContractPharmacy(npi, contractPharmacy)
      .pipe(
        tap(() =>
          patchState({
            pharmacyDetails: {
              ...getState().pharmacyDetails,
              pending: false
            }
          })
        ),
        catchError((e) => {
          patchState({
            pharmacyDetails: {
              ...getState().pharmacyDetails,
              pending: false
            }
          });
          return throwError(e);
        })
      );
  }

  @Action(pharmacyActions.ClearInhousePharmacyDetails)
  onClearInhousePharmacyDetails({ patchState }: StateContext<IClaimsState>) {
    patchState({
      inhousePharmacyDetails: AsyncDataDefault
    });
  }

  @Action(pharmacyActions.GetInhousePharmacyDetails, {
    cancelUncompleted: true
  })
  onGetInhousePharmacyDetails(
    { patchState, getState }: StateContext<IClaimsState>,
    { id340b }: pharmacyActions.GetInhousePharmacyDetails
  ) {
    patchState({
      inhousePharmacyDetails: {
        ...getState().inhousePharmacyDetails,
        pending: true
      }
    });
    return this._claimsHttpService.getInhousePharmacyDetails(id340b).pipe(
      tap((res) =>
        patchState({
          inhousePharmacyDetails: {
            pending: false,
            data: res
          }
        })
      ),
      catchError((e) => {
        patchState({
          inhousePharmacyDetails: {
            ...getState().inhousePharmacyDetails,
            pending: false
          }
        });
        return throwError(e);
      })
    );
  }

  @Action(pharmacyActions.LinkInhousePharmacy)
  onLinkInhousePharmacy(
    { patchState, getState }: StateContext<IClaimsState>,
    { params }: pharmacyActions.LinkInhousePharmacy
  ) {
    patchState({
      inhousePharmacyDetails: {
        ...getState().inhousePharmacyDetails,
        pending: true
      },
      pharmacyDetails: {
        ...getState().pharmacyDetails,
        pending: true
      }
    });
    return this._claimsHttpService.linkInhousePharmacy(params).pipe(
      tap(() =>
        patchState({
          inhousePharmacyDetails: {
            ...getState().inhousePharmacyDetails,
            pending: false
          },
          pharmacyDetails: {
            ...getState().pharmacyDetails,
            pending: false
          }
        })
      ),
      catchError((e) => {
        patchState({
          inhousePharmacyDetails: {
            ...getState().inhousePharmacyDetails,
            pending: false
          },
          pharmacyDetails: {
            ...getState().pharmacyDetails,
            pending: false
          }
        });
        return throwError(e);
      })
    );
  }

  @Action(pharmacyActions.UnlinkInhousePharmacy)
  onUnlinkInhousePharmacy(
    { patchState, getState }: StateContext<IClaimsState>,
    { npi, inhousePharmacy }: pharmacyActions.UnlinkInhousePharmacy
  ) {
    patchState({
      pharmacyDetails: {
        ...getState().pharmacyDetails,
        pending: true
      }
    });
    return this._claimsHttpService
      .unlinkInhousePharmacy(npi, inhousePharmacy)
      .pipe(
        tap(() =>
          patchState({
            pharmacyDetails: {
              ...getState().pharmacyDetails,
              pending: false
            }
          })
        ),
        catchError((e) => {
          patchState({
            pharmacyDetails: {
              ...getState().pharmacyDetails,
              pending: false
            }
          });
          return throwError(e);
        })
      );
  }

  @Action(prescriberActions.ClearPrescriberDetails)
  onClearPrescriberDetails({ patchState }: StateContext<IClaimsState>) {
    patchState({
      prescriberDetails: AsyncDataDefault
    });
  }

  @Action(prescriberActions.GetPrescriberDetails, { cancelUncompleted: true })
  onGetPrescriberDetails(
    { patchState, getState }: StateContext<IClaimsState>,
    { npi }: prescriberActions.GetPrescriberDetails
  ) {
    patchState({
      prescriberDetails: {
        ...getState().prescriberDetails,
        pending: true
      }
    });
    return this._claimsHttpService.getPrescriberDetails(npi).pipe(
      tap((res) =>
        patchState({
          prescriberDetails: {
            pending: false,
            data: res
          }
        })
      ),
      catchError((e) => {
        patchState({
          prescriberDetails: {
            ...getState().prescriberDetails,
            pending: false
          }
        });
        return throwError(e);
      })
    );
  }

  @Action(prescriberActions.AddPresciberCEAssociation)
  onAddPresciberCEAssociation(
    { patchState, getState }: StateContext<IClaimsState>,
    { params }: prescriberActions.AddPresciberCEAssociation
  ) {
    patchState({
      prescriberDetails: {
        ...getState().prescriberDetails,
        pending: true
      }
    });
    return this._claimsHttpService.addPrescriberCEAssociation(params).pipe(
      tap(() =>
        patchState({
          prescriberDetails: {
            ...getState().prescriberDetails,
            pending: false
          }
        })
      ),
      catchError((e) => {
        patchState({
          prescriberDetails: {
            ...getState().prescriberDetails,
            pending: false
          }
        });
        return throwError(e);
      })
    );
  }

  @Action(prescriberActions.RemovePresciberCEAssociation)
  onRemovePresciberCEAssociation(
    { patchState, getState }: StateContext<IClaimsState>,
    { params }: prescriberActions.RemovePresciberCEAssociation
  ) {
    patchState({
      prescriberDetails: {
        ...getState().prescriberDetails,
        pending: true
      }
    });
    return this._claimsHttpService.removePrescriberCEAssociation(params).pipe(
      tap(() =>
        patchState({
          prescriberDetails: {
            ...getState().prescriberDetails,
            pending: false
          }
        })
      ),
      catchError((e) => {
        patchState({
          prescriberDetails: {
            ...getState().prescriberDetails,
            pending: false
          }
        });
        return throwError(e);
      })
    );
  }

  @Action(auditClaimSearchActions.ActionAuditClaimSearchClear)
  onClearAuditClaimSearch({ patchState }: StateContext<IClaimsState>) {
    patchState({
      auditClaimSearch: AsyncDataDefault
    });
  }

  @Action(auditClaimSearchActions.ActionAuditClaimSearchSubmit, {
    cancelUncompleted: true
  })
  onSubmitAuditClaimSearch(
    { patchState, getState }: StateContext<IClaimsState>,
    { params }: auditClaimSearchActions.ActionAuditClaimSearchSubmit
  ) {
    patchState({
      auditClaimSearch: {
        ...getState().auditClaimSearch,
        pending: true
      }
    });
    return this._claimsHttpService.search340BAuditClaim(params).pipe(
      tap((res) =>
        patchState({
          auditClaimSearch: {
            pending: false,
            data: res
          }
        })
      ),
      catchError((e) => {
        patchState({
          auditClaimSearch: {
            ...getState().auditClaimSearch,
            pending: false
          }
        });
        return throwError(e);
      })
    );
  }

  @Action(actions.ClearClaimsUploadList)
  onClearClaimsUploadList({ patchState }: StateContext<IClaimsState>) {
    return patchState({
      claimsUploadData: DataDefault
    });
  }

  @Action(actions.GetClaimsUploadList, { cancelUncompleted: true })
  onGetClaimsUploadList(
    { patchState, getState }: StateContext<IClaimsState>,
    { params }: actions.GetClaimsUploadList
  ) {
    patchState({
      claimsUploadData: {
        ...getState().claimsUploadData,
        pending: true
      }
    });

    return this._claimsHttpService.getClaimsUploadList(params).pipe(
      tap((res) =>
        patchState({
          claimsUploadData: {
            ...res,
            pending: false
          }
        })
      ),
      catchError((e) => {
        patchState({
          claimsUploadData: {
            ...getState().claimsUploadData,
            pending: false
          }
        });
        return throwError(e);
      })
    );
  }

  @Action(actions.DownloadClaimFile)
  onDownloadClaimFile(
    { patchState }: StateContext<IClaimsState>,
    { params }: actions.DownloadClaimFile
  ) {
    patchState({
      claimFileDownload: {
        pending: true
      }
    });

    return this._claimsHttpService.downloadClaimFile(params).pipe(
      tap(() =>
        patchState({
          claimFileDownload: {
            pending: false
          }
        })
      ),
      catchError((e) => {
        patchState({
          claimFileDownload: {
            pending: false
          }
        });
        return throwError(e);
      })
    );
  }

  @Action(actions.DeleteClaimFile)
  onDeleteClaimFile(
    { patchState }: StateContext<IClaimsState>,
    { fileId }: actions.DeleteClaimFile
  ) {
    patchState({
      claimFileDelete: {
        pending: true
      }
    });

    return this._claimsHttpService.deleteClaimFile(fileId).pipe(
      tap(() =>
        patchState({
          claimFileDelete: {
            pending: false
          }
        })
      ),
      catchError((e) => {
        patchState({
          claimFileDelete: {
            pending: false
          }
        });
        return throwError(e);
      })
    );
  }

  @Action(actions.GetClaimFileStatus, { cancelUncompleted: true })
  onGetClaimFileStatus(
    { patchState }: StateContext<IClaimsState>,
    { fileId }: actions.GetClaimFileStatus
  ) {
    patchState({
      claimFileStatus: {
        pending: true,
        data: undefined,
        total: 0
      }
    });

    return this._claimsHttpService.getClaimFileStatus(fileId).pipe(
      tap((status) =>
        patchState({
          claimFileStatus: {
            pending: false,
            data: status,
            total: 1
          }
        })
      ),
      catchError((e) => {
        patchState({
          claimFileStatus: {
            pending: false,
            data: undefined,
            total: 0
          }
        });
        return throwError(e);
      })
    );
  }

  @Action(actions.ReverseClaimUpload)
  onReverseClaimUpload(
    { patchState, getState }: StateContext<IClaimsState>,
    { claimUploadId }: actions.ReverseClaimUpload
  ) {
    return this._claimsHttpService.reverseClaim(claimUploadId).pipe(
      tap(() =>
        patchState(
          produce(getState(), (draft) => {
            const target = draft.claimsUploadData.data.find(
              (claim) => claim.claimLogId === claimUploadId
            );
            if (target) {
              target.status = ENUM_CLAIM_UPLOAD_STATUS.REVERSED;
              target.reversedBy = 'Current User';
              target.reversalDate = new Date();
            }
          })
        )
      )
    );
  }

  @Action(actions.ClearClaimsGroupsList)
  onClearClaimsGroupsList({ patchState }: StateContext<IClaimsState>) {
    return patchState({
      claimsGroupsDataV2: DataDefault
    });
  }

  @Action(actions.GetClaimsManagementDataV2, { cancelUncompleted: true })
  onGetClaimsManagementDataV2(
    context: StateContext<IClaimsState>,
    { params, queryClaimsList }: actions.GetClaimsManagementDataV2
  ) {
    return queryClaimsList
      ? this.onGetClaimsGroupClaimsListV2(context, params)
      : this.onGetClaimsGroupsListV2(context, params);
  }

  onGetClaimsGroupsListV2(
    { patchState, getState }: StateContext<IClaimsState>,
    params: ClaimsGroupListParamsV2
  ) {
    patchState({
      claimsGroupsDataV2: {
        ...getState().claimsGroupsDataV2,
        pending: true
      }
    });

    return (
      params.serviceType === CustomerServiceType.AUDIT_340B
        ? this._claimsHttpService
            .getClaimsGroupClaimsListV2({
              ...params,
              take: 1,
              skip: 0,
              sort: [],
              filters: {}
            })
            .pipe(
              map((res) => ({
                ...res,
                data: [
                  {
                    ClaimCount: res.total,
                    TotalWAC: 0,
                    TotalRebate: 0,
                    PriceProtectValue: 0,
                    TotalAdminValue: 0,
                    TotalRebateValue: 0
                  }
                ]
              }))
            )
        : this._claimsHttpService.getClaimsGroupsListV2(params)
    ).pipe(
      tap((res) => {
        patchState({
          claimsGroupsDataV2: {
            ...res,
            pending: false
          }
        });
      }),
      catchError((e) => {
        patchState({
          claimsGroupsDataV2: {
            ...getState().claimsGroupsDataV2,
            pending: false
          }
        });
        return throwError(e);
      })
    );
  }

  onGetClaimsGroupClaimsListV2(
    { patchState, getState }: StateContext<IClaimsState>,
    params: ClaimsGroupListParamsV2
  ) {
    patchState({
      claimsGroupClaimsDataV2: {
        ...getState().claimsGroupClaimsDataV2,
        pending: true
      }
    });

    return this._claimsHttpService.getClaimsGroupClaimsListV2(params).pipe(
      tap((res) =>
        patchState({
          claimsGroupClaimsDataV2: {
            ...res,
            pending: false
          }
        })
      ),
      catchError((e) => {
        patchState({
          claimsGroupClaimsDataV2: {
            ...getState().claimsGroupClaimsDataV2,
            pending: false
          }
        });
        return throwError(e);
      })
    );
  }

  @Action(actions.ClearClaimsList)
  onClearClaimsList({ patchState }: StateContext<IClaimsState>) {
    return patchState({
      claimsData: DataDefault,
      claimsGroupClaimsDataV2: DataDefault
    });
  }

  @Action(actions.GetClaimsList)
  onGetClaimsList(
    { patchState, getState }: StateContext<IClaimsState>,
    {
      userId,
      fileId,
      skip,
      take,
      entityId,
      statusCode,
      sort,
      serviceType
    }: actions.GetClaimsList
  ) {
    patchState({
      claimsData: {
        ...getState().claimsData,
        pending: true
      }
    });

    return this._claimsHttpService
      .getClaimsList(
        userId,
        fileId,
        skip,
        take,
        entityId,
        statusCode,
        sort,
        serviceType
      )
      .pipe(
        tap((res) =>
          patchState({
            claimsData: {
              ...res,
              pending: false
            }
          })
        ),
        catchError((e) => {
          patchState({
            claimsData: {
              ...getState().claimsData,
              pending: false
            }
          });
          return throwError(e);
        })
      );
  }

  @Action(actions.ClearClaimsCustomersList)
  onClearClaimsCustomersList({ patchState }: StateContext<IClaimsState>) {
    return patchState({
      claimsCustomersData: ClaimCustomersDefault
    });
  }

  @Action(actions.GetClaimsCustomersList)
  onGetClaimsCustomersList(
    { patchState, getState }: StateContext<IClaimsState>,
    { skip, take, user, sort, filter }: actions.GetClaimsCustomersList
  ) {
    patchState({
      claimsCustomersData: {
        ...getState().claimsCustomersData,
        pending: true
      }
    });

    return this._claimsHttpService
      .getClaimsCustomersList(skip, take, user, sort, filter)
      .pipe(
        tap((res) => {
          patchState({
            claimsCustomersData: {
              take: 0,
              data: res.data,
              skip: 0,
              total: res.count[0].count,
              pending: false
            }
          });
        })
      );
  }

  @Action(actions.ClearClaimDetails)
  onClearClaimDetails({ patchState }: StateContext<IClaimsState>) {
    return patchState({
      claimDetailsData: ClaimDetailsDataDefault
    });
  }

  @Action(actions.uploadClaim)
  onUploadClaim(
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    { patchState, getState }: StateContext<IClaimsState>,
    { claimsDocuments, code }: actions.uploadClaim
  ) {
    return this._claimsHttpService.uploadClaimIntoClaimGroup(
      code,
      claimsDocuments
    );
  }

  @Action(actions.GetClaimDetails)
  onGetClaimDetails(
    { patchState, getState }: StateContext<IClaimsState>,
    { userId, claimIntakeId }: actions.GetClaimDetails
  ) {
    patchState({
      claimDetailsData: {
        ...getState().claimDetailsData,
        pending: true
      }
    });

    return this._claimsHttpService.getClaimDetails(userId, claimIntakeId).pipe(
      tap((res) =>
        patchState({
          claimDetailsData: {
            ...getState().claimDetailsData,
            data: res,
            pending: false
          }
        })
      ),
      catchError((e) => {
        patchState({
          claimDetailsData: {
            ...getState().claimDetailsData,
            pending: false
          }
        });
        return throwError(e);
      })
    );
  }

  @Action(actions.UpdateClaim)
  onUpdateClaim(
    { patchState, getState }: StateContext<IClaimsState>,
    { params }: actions.UpdateClaim
  ) {
    patchState({
      claimUpdateData: {
        ...getState().claimUpdateData,
        pending: true
      }
    });

    return this._claimsHttpService.updateClaimsStatusV2(params).pipe(
      tap(() =>
        patchState({
          claimUpdateData: {
            ...getState().claimUpdateData,
            pending: false
          }
        })
      ),
      catchError((e) => {
        patchState({
          claimUpdateData: {
            ...getState().claimUpdateData,
            pending: false
          }
        });
        return throwError(e);
      })
    );
  }

  @Action(actions.Update340BAuditSingleClaim)
  update340BAuditSingleClaim(
    { patchState }: StateContext<IClaimsState>,
    { params }: actions.Update340BAuditSingleClaim
  ) {
    patchState({
      auditClaimUpdate: {
        pending: true
      }
    });

    return this._claimsHttpService.update340BAuditSingleClaim(params).pipe(
      tap(() =>
        patchState({
          auditClaimUpdate: {
            pending: false
          }
        })
      ),
      catchError((e) => {
        patchState({
          auditClaimUpdate: {
            pending: false
          }
        });
        return throwError(e);
      })
    );
  }

  @Action(actions.Complete340BAuditManualReviewSingleClaim)
  complete340BAuditManualReviewSingleClaim(
    { patchState }: StateContext<IClaimsState>,
    { intakeId }: actions.Complete340BAuditManualReviewSingleClaim
  ) {
    patchState({
      auditClaimManualReviewComplete: {
        pending: true
      }
    });

    return this._claimsHttpService
      .complete340BAuditManualReviewSingleClaim(intakeId)
      .pipe(
        tap(() =>
          patchState({
            auditClaimManualReviewComplete: {
              pending: false
            }
          })
        ),
        catchError((e) => {
          patchState({
            auditClaimManualReviewComplete: {
              pending: false
            }
          });
          return throwError(e);
        })
      );
  }

  @Action(actions.GetClaimsSummary, { cancelUncompleted: true })
  onGetClaimsSummary(
    { patchState, getState }: StateContext<IClaimsState>,
    { params }: actions.GetClaimsSummary
  ) {
    patchState({
      claimsSummaryData: {
        ...getState().claimsSummaryData,
        pending: true
      }
    });

    return this._claimsHttpService.getClaimsSummary(params).pipe(
      tap((res) =>
        patchState({
          claimsSummaryData: {
            ...res,
            pending: false
          }
        })
      ),
      catchError((e) => {
        patchState({
          claimsSummaryData: {
            ...getState().claimsSummaryData,
            pending: false
          }
        });
        return throwError(e);
      })
    );
  }

  @Action(actions.GetClaimsSummaryBatchStatistics)
  onGetClaimsSummaryBatchStatistics(
    { patchState, getState }: StateContext<IClaimsState>,
    { params }: actions.GetClaimsSummaryBatchStatistics
  ) {
    const currentState = getState().claimsSummaryBatchStatisticsData;
    currentState.set(params.level, {
      ...currentState.get(params.level)!,
      pending: true
    });
    patchState({
      claimsSummaryBatchStatisticsData: currentState
    });

    return this._claimsHttpService.getClaimsSummaryBatchStatistics(params).pipe(
      tap((res) => {
        let data = [];
        if (params.category === 'PrescriberName') {
          data = res.data.prescriberData;
        } else if (params.category === 'PharmacyName') {
          data = res.data.pharmacyData;
        } else {
          data = res.data.data;
        }
        currentState.set(params.level, {
          ...res,
          totalClaims: res.data.totalClaims,
          data: data,
          insights: res.data.insights,
          pending: false
        });
        patchState({
          claimsSummaryBatchStatisticsData: currentState
        });
      }),
      catchError((e) => {
        currentState.set(params.level, {
          ...currentState.get(params.level)!,
          pending: false
        });
        patchState({
          claimsSummaryBatchStatisticsData: currentState
        });
        return throwError(e);
      })
    );
  }

  @Action(actions.ClearClaimsSummaryBatchStatistics)
  onClearClaimsSummaryBatchStatistics(
    { patchState, getState }: StateContext<IClaimsState>,
    { level }: actions.ClearClaimsSummaryBatchStatistics
  ) {
    const newState = getState().claimsSummaryBatchStatisticsData;

    if (level) {
      newState.set(level, BatchLevelStatisticsDefault);
    } else {
      Object.values(BatchStatisticsLevel).forEach((statLevel) =>
        newState.set(statLevel, BatchLevelStatisticsDefault)
      );
    }

    return patchState({
      claimsSummaryBatchStatisticsData: newState
    });
  }

  @Action(actions.GetAuditClaimFiles, { cancelUncompleted: true })
  onGetAuditClaimFiles(
    { patchState, getState }: StateContext<IClaimsState>,
    { params }: actions.GetAuditClaimFiles
  ) {
    patchState({
      auditClaimFileData: {
        ...getState().auditClaimFileData,
        data: [],
        total: 0,
        pending: true
      }
    });

    return this._claimsHttpService.get340BAuditClaimsFiles(params).pipe(
      tap((res) =>
        patchState({
          auditClaimFileData: {
            data: res || [],
            total: (res || []).length,
            pending: false
          }
        })
      ),
      catchError((e) => {
        patchState({
          auditClaimFileData: {
            ...getState().auditClaimFileData,
            pending: false
          }
        });
        return throwError(e);
      })
    );
  }

  @Action(actions.GetReviewedAuditClaimFiles, { cancelUncompleted: true })
  onGetReviewedAuditClaimFiles(
    { patchState, getState }: StateContext<IClaimsState>,
    { customerId }: actions.GetReviewedAuditClaimFiles
  ) {
    patchState({
      auditReviewedClaimFileData: {
        ...getState().auditReviewedClaimFileData,
        data: [],
        total: 0,
        pending: true
      }
    });

    return this._claimsHttpService
      .get340BAuditCustomerReviewedFiles(customerId)
      .pipe(
        tap((res) =>
          patchState({
            auditReviewedClaimFileData: {
              data: res || [],
              total: (res || []).length,
              pending: false
            }
          })
        ),
        catchError((e) => {
          patchState({
            auditReviewedClaimFileData: {
              ...getState().auditReviewedClaimFileData,
              pending: false
            }
          });
          return throwError(e);
        })
      );
  }

  @Action(actions.GetAuditClaimTotals, { cancelUncompleted: true })
  onGetAuditClaimTotals(
    { patchState, getState }: StateContext<IClaimsState>,
    { customerId, filters }: actions.GetAuditClaimTotals
  ) {
    patchState({
      auditClaimTotalsData: {
        ...getState().auditClaimTotalsData,
        data: undefined,
        total: 0,
        pending: true
      }
    });

    return this._claimsHttpService
      .get340BAuditCustomerReviewClaimsTotals(customerId, filters)
      .pipe(
        tap((res) =>
          patchState({
            auditClaimTotalsData: {
              data: res,
              total: 0,
              pending: false
            }
          })
        ),
        catchError((e) => {
          patchState({
            auditClaimTotalsData: {
              ...getState().auditClaimTotalsData,
              pending: false
            }
          });
          return throwError(e);
        })
      );
  }

  @Action(actions.GetAuditReportingStatusTotals, { cancelUncompleted: true })
  onGetAuditReportingStatusTotals(
    { patchState, getState }: StateContext<IClaimsState>,
    { customerId, filters }: actions.GetAuditReportingStatusTotals
  ) {
    patchState({
      auditReportingStatusTotals: {
        ...getState().auditReportingStatusTotals,
        pending: true
      }
    });

    return this._claimsHttpService
      .getAuditReportingStatusTotals(customerId, filters)
      .pipe(
        tap((res) =>
          patchState({
            auditReportingStatusTotals: {
              data: res,
              total: 0,
              pending: false
            }
          })
        ),
        catchError((e) => {
          patchState({
            auditReportingStatusTotals: {
              ...getState().auditReportingStatusTotals,
              pending: false
            }
          });
          return throwError(e);
        })
      );
  }

  @Action(actions.GetAuditReportingResolutionTrends, {
    cancelUncompleted: true
  })
  onGetAuditReportingResolutionTrends(
    { patchState, getState }: StateContext<IClaimsState>,
    { customerId, filters }: actions.GetAuditReportingResolutionTrends
  ) {
    patchState({
      auditReportingResolutionTrends: {
        ...getState().auditReportingResolutionTrends,
        pending: true
      }
    });

    return this._claimsHttpService
      .getAuditReportingResolutionTrends(customerId, filters)
      .pipe(
        tap((res) =>
          patchState({
            auditReportingResolutionTrends: {
              data: res,
              total: 0,
              pending: false
            }
          })
        ),
        catchError((e) => {
          patchState({
            auditReportingResolutionTrends: {
              ...getState().auditReportingResolutionTrends,
              pending: false
            }
          });
          return throwError(e);
        })
      );
  }

  @Action(actions.GetAuditReportingTop340B, { cancelUncompleted: true })
  onGetAuditReportingTop340B(
    { patchState, getState }: StateContext<IClaimsState>,
    { customerId, filters }: actions.GetAuditReportingTop340B
  ) {
    patchState({
      auditReportingTop340B: {
        ...getState().auditReportingTop340B,
        pending: true
      }
    });

    return this._claimsHttpService
      .getAuditReportingTop340B(customerId, filters)
      .pipe(
        tap((res) =>
          patchState({
            auditReportingTop340B: {
              data: res,
              total: 0,
              pending: false
            }
          })
        ),
        catchError((e) => {
          patchState({
            auditReportingTop340B: {
              ...getState().auditReportingTop340B,
              pending: false
            }
          });
          return throwError(e);
        })
      );
  }

  @Action(actions.GetAuditReportingTopNot340B, { cancelUncompleted: true })
  onGetAuditReportingTopNot340B(
    { patchState, getState }: StateContext<IClaimsState>,
    { customerId, filters }: actions.GetAuditReportingTopNot340B
  ) {
    patchState({
      auditReportingTopNot340B: {
        ...getState().auditReportingTopNot340B,
        pending: true
      }
    });

    return this._claimsHttpService
      .getAuditReportingTopNot340B(customerId, filters)
      .pipe(
        tap((res) =>
          patchState({
            auditReportingTopNot340B: {
              data: res,
              total: 0,
              pending: false
            }
          })
        ),
        catchError((e) => {
          patchState({
            auditReportingTopNot340B: {
              ...getState().auditReportingTopNot340B,
              pending: false
            }
          });
          return throwError(e);
        })
      );
  }

  @Action(actions.GetAuditReportingTopDisputed, { cancelUncompleted: true })
  onGetAuditReportingTopDisputed(
    { patchState, getState }: StateContext<IClaimsState>,
    { customerId, filters }: actions.GetAuditReportingTopDisputed
  ) {
    patchState({
      auditReportingTopDisputed: {
        ...getState().auditReportingTopDisputed,
        pending: true
      }
    });

    return this._claimsHttpService
      .getAuditReportingTopDisputed(customerId, filters)
      .pipe(
        tap((res) =>
          patchState({
            auditReportingTopDisputed: {
              data: res,
              total: 0,
              pending: false
            }
          })
        ),
        catchError((e) => {
          patchState({
            auditReportingTopDisputed: {
              ...getState().auditReportingTopDisputed,
              pending: false
            }
          });
          return throwError(e);
        })
      );
  }

  @Action(actions.GetAuditReportingTopDisputeResolution, {
    cancelUncompleted: true
  })
  onGetAuditReportingTopDisputeResolution(
    { patchState, getState }: StateContext<IClaimsState>,
    { customerId, filters }: actions.GetAuditReportingTopDisputeResolution
  ) {
    patchState({
      auditReportingTopDisputeResolution: {
        ...getState().auditReportingTopDisputeResolution,
        pending: true
      }
    });

    return this._claimsHttpService
      .getAuditReportingTopDisputeResolution(customerId, filters)
      .pipe(
        tap((res) =>
          patchState({
            auditReportingTopDisputeResolution: {
              data: res,
              total: 0,
              pending: false
            }
          })
        ),
        catchError((e) => {
          patchState({
            auditReportingTopDisputeResolution: {
              ...getState().auditReportingTopDisputeResolution,
              pending: false
            }
          });
          return throwError(e);
        })
      );
  }

  @Action(actions.ClearAuditReporting)
  onClearAuditReporting({ patchState }: StateContext<IClaimsState>) {
    return patchState({
      auditReportingStatusTotals: NonPaginatedDataUndefinedDefault,
      auditReportingResolutionTrends: NonPaginatedDataDefault,
      auditReportingTop340B: NonPaginatedDataUndefinedDefault,
      auditReportingTopNot340B: NonPaginatedDataUndefinedDefault,
      auditReportingTopDisputed: NonPaginatedDataUndefinedDefault,
      auditReportingTopDisputeResolution: NonPaginatedDataUndefinedDefault
    });
  }

  @Action(claimCEActions.Get340BAuditClaimsCEs, { cancelUncompleted: true })
  onGet340BAuditClaimsCEs(
    { patchState, getState }: StateContext<IClaimsState>,
    { params }: claimCEActions.Get340BAuditClaimsCEs
  ) {
    patchState({
      auditClaimsCEs: {
        ...getState().auditClaimsCEs,
        pending: true
      }
    });

    return this._claimsHttpService.get340BAuditClaimsCEs(params).pipe(
      tap((res) =>
        patchState({
          auditClaimsCEs: {
            data: res,
            pending: false
          }
        })
      ),
      catchError((e) => {
        patchState({
          auditClaimsCEs: {
            ...getState().auditClaimsCEs,
            pending: false
          }
        });
        return throwError(e);
      })
    );
  }

  @Action(claimCEActions.Clear340BAuditClaimsCEs)
  onClear340BAuditClaimsCEs({ patchState }: StateContext<IClaimsState>) {
    return patchState({ auditClaimsCEs: AsyncDataDefault });
  }

  @Action(claimCEActions.Contact340BAuditClaimsCEs)
  onContact340BAuditClaimsCEs(
    { patchState }: StateContext<IClaimsState>,
    { params }: claimCEActions.Contact340BAuditClaimsCEs
  ) {
    patchState({
      auditClaimsCEsContact: {
        pending: true
      }
    });

    return this._claimsHttpService.contact340BAuditClaimsCEs(params).pipe(
      tap(() =>
        patchState({
          auditClaimsCEsContact: {
            pending: false
          }
        })
      ),
      catchError((e) => {
        patchState({
          auditClaimsCEsContact: {
            pending: false
          }
        });
        return throwError(e);
      })
    );
  }

  @Action(mfpClaimsActions.ClearMFPClaims)
  onClearMFPClaims({ patchState }: StateContext<IClaimsState>) {
    return patchState({ mfpClaims: DataDefault });
  }

  @Action(mfpClaimsActions.GetMFPClaims)
  onGetMFPClaims(
    { patchState, getState }: StateContext<IClaimsState>,
    { params }: mfpClaimsActions.GetMFPClaims
  ) {
    patchState({
      mfpClaims: {
        ...getState().mfpClaims,
        pending: true
      }
    });

    return this._claimsHttpService.getMFPClaims(params).pipe(
      tap((res) =>
        patchState({
          mfpClaims: {
            pending: false,
            skip: res.skip,
            take: res.take,
            total: res.total,
            data: res.data
          }
        })
      ),
      catchError((e) => {
        patchState({
          mfpClaims: {
            ...getState().mfpClaims,
            pending: false
          }
        });
        return throwError(e);
      })
    );
  }

  @Action(mfpClaimsActions.UpdateMFPClaims)
  onUpdateMFPClaims(
    { patchState, getState }: StateContext<IClaimsState>,
    { params }: mfpClaimsActions.UpdateMFPClaims
  ) {
    patchState({
      mfpClaimsUpdate: {
        ...getState().mfpClaimsUpdate,
        pending: true
      }
    });

    return this._claimsHttpService.updateMFPClaims(params).pipe(
      tap(() =>
        patchState({
          mfpClaimsUpdate: {
            ...getState().mfpClaimsUpdate,
            pending: false
          }
        })
      ),
      catchError((e) => {
        patchState({
          mfpClaimsUpdate: {
            ...getState().mfpClaimsUpdate,
            pending: false
          }
        });
        return throwError(e);
      })
    );
  }
}
